import type { CarePlanTemplate } from '@troyai/case-notes/data-access';
import type { SuggestedHRAMeta } from '@troyai/hra/data-access';
import { DEMO_ACTORS, storyDate, storyIso } from '@troyai/portal/investor-demo/core';

import { templateId, templateMetaId } from './ids';

/**
 * The care plan template behind each of the 12 recommendations. The HRA drawer looks the template
 * up before it opens a recommendation for review, so the list has to carry them; the barrier,
 * goal and intervention entries fill the review drawer's sections.
 */

interface TemplateRow {
  row: number;
  name: string;
  barrier: string;
  goal: string;
  intervention: string;
  content: string;
}

const ROWS: TemplateRow[] = [
  {
    row: 1,
    name: 'CHF medication review: stop meloxicam (NSAID with CHF and CKD 3a)',
    barrier: 'Takes an NSAID for knee pain despite CHF and CKD stage 3a',
    goal: 'Off all oral NSAIDs within 2 weeks',
    intervention: 'Pharmacist review, prescriber outreach, topical diclofenac alternative',
    content:
      'Stop meloxicam with the orthopedist and move the knee pain to topical diclofenac. Watch daily weights and ED visits for 30 days.',
  },
  {
    row: 2,
    name: 'Transportation support (ride benefit)',
    barrier: 'No car since June and no way to reach appointments',
    goal: 'Ride benefit set up before the next follow-up visit',
    intervention: 'Enroll her in the plan ride benefit and book the cardiology visit rides',
    content: 'Set up the ride benefit and confirm the first two rides with the member.',
  },
  {
    row: 3,
    name: 'Annual diabetic eye exam',
    barrier: 'No eye exam on record this year',
    goal: `Dilated eye exam completed by ${storyDate('2026-12-15', 'MMM d')}`,
    intervention: 'Schedule the exam, arrange the ride, send the results request',
    content: 'Book a dilated eye exam and close the gap once the claim arrives.',
  },
  {
    row: 4,
    name: 'Pneumococcal vaccination (PCV20)',
    barrier: 'No pneumococcal vaccine on record',
    goal: 'PCV20 given within 90 days',
    intervention: 'Discuss with her PCP, offer the pharmacy vaccination',
    content: 'Offer PCV20 at the pharmacy or her PCP and record the date given.',
  },
  {
    row: 5,
    name: 'Flu shot reminder',
    barrier: 'No flu vaccine on record this season',
    goal: 'Flu shot given this season',
    intervention: 'Reminder call and pharmacy vaccination',
    content: 'Remind her about the flu shot and offer it at her pharmacy.',
  },
  {
    row: 6,
    name: 'Home blood pressure cuff',
    barrier: 'No home blood pressure readings',
    goal: 'Home readings logged three times a week',
    intervention: 'Supply a cuff and teach the readings',
    content: 'Send a home blood pressure cuff and review the first week of readings.',
  },
  {
    row: 7,
    name: 'Extra Help / benefits check',
    barrier: 'No Extra Help on file despite nine monthly medications',
    goal: 'Eligibility checked and application filed if she qualifies',
    intervention: 'Benefits screening with the member',
    content: 'Screen for Extra Help and help her apply if she qualifies.',
  },
  {
    row: 8,
    name: 'Statin dose increase (atorvastatin 20 → 40 mg)',
    barrier: 'Atorvastatin fills have gaps',
    goal: 'LDL at goal on a tolerated dose',
    intervention: 'Prescriber request to raise the dose',
    content: 'Ask the prescriber about 40 mg once the fills are steady.',
  },
  {
    row: 9,
    name: 'Statin adherence outreach',
    barrier: `Atorvastatin adherence is 71%, supply ran out ${storyDate('2026-09-25', 'MMM d')}`,
    goal: 'Adherence above 80%',
    intervention: 'Call to find the reason and arrange a refill',
    content: 'Call the member about the missed statin refills and arrange a refill.',
  },
  {
    row: 10,
    name: 'A1c recheck',
    barrier: 'Last A1c was 8.4 with no recheck since',
    goal: 'A1c rechecked this quarter',
    intervention: 'Order or request the lab and review the result',
    content: 'Arrange an A1c recheck and review the result with her PCP.',
  },
  {
    row: 11,
    name: 'Fall-risk screen',
    barrier: 'Lives alone, nine medications including a diuretic',
    goal: 'Fall-risk screen done and home risks addressed',
    intervention: 'Screening call, medication review for dizziness',
    content: 'Complete a fall-risk screen and review medications that cause dizziness.',
  },
  {
    row: 12,
    name: 'PPI deprescribing review',
    barrier: 'Omeprazole filled alongside meloxicam since June',
    goal: 'Need for the PPI reviewed once the NSAID stops',
    intervention: 'Pharmacist review and prescriber recommendation',
    content: 'Review the long-term PPI once meloxicam has stopped.',
  },
];

const AUTHOR = DEMO_ACTORS.lisa.email;
const CREATED = storyIso('2026-01-12T09:00:00');

/** The template's entries as the suggestion's `suggested_hra_meta` carries them. */
export function suggestedMeta(row: number): SuggestedHRAMeta[] {
  const spec = ROWS.find((candidate) => candidate.row === row);
  if (!spec) {
    return [];
  }
  return [
    { id: templateMetaId(row, 1), title: spec.barrier, type: 'barrier' },
    { id: templateMetaId(row, 2), title: spec.goal, type: 'goal' },
    { id: templateMetaId(row, 3), title: spec.intervention, type: 'intervention' },
  ];
}

export function suggestedContent(row: number): string {
  return ROWS.find((candidate) => candidate.row === row)?.content ?? '';
}

export function seedCarePlanTemplates(): CarePlanTemplate[] {
  return ROWS.map((spec): CarePlanTemplate => ({
    id: templateId(spec.row),
    name: spec.name,
    subject: spec.name,
    content: spec.content,
    created_at: CREATED,
    created_by: AUTHOR,
    last_modified: CREATED,
    last_modified_by: AUTHOR,
    hra_meta: suggestedMeta(spec.row),
  }));
}
